import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { AiOutlineFileImage, AiOutlineFilePdf } from "react-icons/ai";
import { LiaFileUploadSolid } from "react-icons/lia";
import { useParams } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Button,
 ComboboxItem,  Group,
  Modal,
  ScrollAreaAutosize,
  Select,
  Text } from "@mantine/core";
import { IconInfoCircle, IconTrash } from "@tabler/icons-react";

import {
  showLoadingNotification,
  showSuccessNotification,
} from "@utils/showNotification";
import { truncateWithEllipsis } from "@utils/truncateText";

import { getFileTagsValues } from "@data/dropdownValues";

import { documentFileTypes, imageFileTypes } from "@constants/fileTypes";
import { MAX_FILENAME_LENGTH_BEFORE_UPLOAD, MEGABYTE } from "@constants/index";

import { useUpdateConsultation } from "@hooks/mutations/consultation/useUpdateConsultation";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { StyledAddFileModalContent } from "./AddFileModal.styled";


interface AddModalProps {
  opened: boolean;
  close: () => void;
}

export const AddFileModal = ({ opened, close }: AddModalProps) => {
  const { t } = useTranslation();
  const { consultationId } = useParams();

  const [uploadedFiles, setUploadedFiles] = useState<File[] | []>([]);
  const [tag, setTag] = useState<ComboboxItem | null>(null);

  const { mutate: updateConsultation, isLoading: isUpdateConsultationLoading } =
    useUpdateConsultation();
  const handleErrors = useQueryErrorHandling();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadedFiles([
        ...uploadedFiles,
        ...acceptedFiles.map(file => {
          const blob = file.slice(0, file.size, file.type);
          const newFile = new File([blob], tag?.value + "-" + file.name, {
            type: file.type,
          });
          return newFile;
        }),
      ]);
    },
    [uploadedFiles, tag]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: {
      "image/*": imageFileTypes,
      "application/pdf": documentFileTypes,
    },
    onDrop,
    disabled: !tag,
  });

  const removeFile = (file: File) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
  };

  const saveFiles = () => {
    if (!tag || !uploadedFiles.length) return;

    const jsFormData = new FormData();
    uploadedFiles?.length &&
      uploadedFiles.forEach(file => {
        jsFormData.append(`files`, file);
      });

    const id = showLoadingNotification({});
    updateConsultation(
      {
        consultationId: consultationId as string,
        formData: jsFormData,
      },
      {
        onSuccess: () => {
          // state.selectedConsultation = null;
          showSuccessNotification({
            id,
            title: "success",
            message: "update-consultation-success",
          });
          setUploadedFiles([]);
          close();
        },
        onError: (res: any) => {
          handleErrors(res, "mutation", id);
        },
      }
    );
  };

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("add-file")}</Text>}
      opened={opened}
      onClose={close}
      scrollAreaComponent={ScrollAreaAutosize}>
      <StyledAddFileModalContent>
        <Select
          withAsterisk
          label={t("file-tag")}
          placeholder={t("select-file-tag")}
          data={getFileTagsValues()}
          value={tag?.value ?? null}
          onChange={(_val, option) => setTag(option)}
        />

        <div className="files-wrapper">
          <div
            {...getRootProps({
              className: `dropzone ${isDragActive ? "active" : ""} ${
                isDragAccept ? "accept" : ""
              } ${isDragReject ? "reject" : ""} ${!tag ? "disabled" : ""}`,
            })}>
            <input {...getInputProps()} disabled={!tag} />

            <div className="text-content">
              <div className="svg-wrapper">
                <LiaFileUploadSolid />
              </div>
              <div className="p-wrapper">
                <p>{t("drag-file-here")}</p>
                <p>{t("drag-file-here-continuation")}</p>
              </div>
            </div>
          </div>
          <div className="uploaded-files">
            <h4>{t("uploaded-files")}</h4>
            {!uploadedFiles.length ? (
              <Alert
                mt={"md"}
                variant="light"
                color="blue"
                icon={<IconInfoCircle />}>
                <Text fz={14} lh={1.5} c="blue">
                  {t("files-appear-info")}
                </Text>
              </Alert>
            ) : (
              <ul>
                {uploadedFiles.map(file => (
                  <li key={file.name}>
                    <div className="content">
                      {file.type === "application/pdf" ? (
                        <AiOutlineFilePdf />
                      ) : (
                        <AiOutlineFileImage />
                      )}
                      <p>
                        {truncateWithEllipsis(
                          file.name,
                          MAX_FILENAME_LENGTH_BEFORE_UPLOAD
                        )}{" "}
                        ({(file.size / MEGABYTE).toFixed(2)} {t("mb")})
                      </p>
                    </div>

                    <ActionIcon
                      variant="outline"
                      color="red"
                      onClick={() => removeFile(file)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <Group justify="end" mt={"xl"} pb={"md"}>
          <PermissionGate permission="consultations.edit"><Button
            onClick={() => saveFiles()}
            disabled={!uploadedFiles.length}
            loading={isUpdateConsultationLoading}>
            {t("submit")}
          </Button></PermissionGate>
        </Group>
      </StyledAddFileModalContent>
    </Modal>
  );
};
